import React, {
  FunctionComponent, useEffect, useState, Fragment,
  useRef,
  useCallback
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { GetRowIdParams, GridReadyEvent, IServerSideGetRowsParams } from '@ag-grid-enterprise/all-modules';
import { RootState } from 'data/root.reducers';
import { GridSettings, WithAuditDetails, AuditDetails } from 'data/types';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { DateRange, DateRangePicker, IDateRangeShortcut } from '@blueprintjs/datetime';
import { ActiveAlarm } from 'data/alarm/alarm.types';
import {
  AnchorButton, Classes, Intent,
} from '@blueprintjs/core';
import moment from 'moment';
import { AgGridReact } from '@ag-grid-community/react';

import { Popover2 } from '@blueprintjs/popover2';
import ServerSideDatasource from 'shared/commonGrid/serverSideDatasource';
import { getHistoricalAlarms } from 'data/alarm/alarm.api';
import { historicalAlarmsColumnDefs } from 'routes/Alarm/Alarm.types';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {};

const connector = connect(mapState, mapDispatch);

const PAGE_SIZE = 100;

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const HistoricalAlarmList: FunctionComponent<Props> = ({
  history
}: Props) => {
  const gridRef = useRef<any>(null);
  const startOfDay = new Date();
  startOfDay.setDate(startOfDay.getDate() - 7);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>([startOfDay, endOfDay]);
  const [selectedShortcutIndex, setSelectedShortcutIndex] = useState<number>(0);
  const [dateRangePopupOpen, setDateRangePopupOpen] = useState<boolean>(false);
  const [dateRangeButtonText, setDateRangeButtonText] = useState<string>(`${moment(startOfDay).format('ll')} - ${moment(endOfDay).format('ll')}`);
  const [selectedActiveAlarms, setSelectedActiveAlarms] = useState<ActiveAlarm[]>([]);

  const getServerSideDataSource = useCallback(() => {
    return {
      getRows(params: IServerSideGetRowsParams) {
        const { request } = params;
        const dataSource = new ServerSideDatasource({});
        const options = dataSource.buildParamsFromJsonForXREST(request);
        const queryParams = new URLSearchParams(options);
        const dateRangeFilterCriteria = buildDateRangeFilterCriteria(selectedDateRange);
        const body = queryParams.get('$filter') != null ? `${options} and ${dateRangeFilterCriteria}` : `${options}&$filter=${dateRangeFilterCriteria}`;
        getHistoricalAlarms(body)
          .then((r) => {
            const rowData = r.data?.data || [];
            const rowCount = r.data?.metadata?.recordsInView || 0;
            params.success({ rowData, rowCount });
          })
          .catch((r) => {
            params.fail();
          });
      }
    };
  }, [selectedDateRange]);

  const buildDateRangeFilterCriteria = (
    dateRange: DateRange,
  ): string => {
    const thenextDate = new Date(); // get the next date to compare since db will compare time at 00:00
    if (dateRange[1]) {
      thenextDate.setDate(dateRange[1].getDate() + 1);
    }
    return `(datetime ge '${moment(dateRange[0]).format('YYYY-MM-DD')}') and (datetime lt '${moment(thenextDate).format('YYYY-MM-DD')}')`;
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setServerSideDatasource(getServerSideDataSource());
    }
  }, [getServerSideDataSource]);

  const onGridReady = useCallback((grid: GridReadyEvent) => {
    grid.columnApi.applyColumnState({
      state: [{ colId: 'datetime', sort: 'desc', sortIndex: 0 }],
      defaultState: { sort: null },
    })
    grid.api.setServerSideDatasource(getServerSideDataSource());
  }, []);

  const handleShortcutChange = (shortcut: IDateRangeShortcut, index: number) => {
    setSelectedShortcutIndex(index);
    setDateRangeButtonText(shortcut.label);
  };

  const handleDateChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange);
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }
    if (gridRef.current) {
      setDateRangeButtonText(`${moment(dateRange[0]).format('ll')} - ${moment(dateRange[1]).format('ll')}`);
      setDateRangePopupOpen(false);
    }
  };

  const handleInteraction = (isOpen: boolean) => {
    setDateRangePopupOpen(isOpen);
  }

  const handleRefresh = () => {
    refreshTable();
  };

  const refreshTable = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshServerSideStore({ purge: true });
    }
  }

  const onSelectionChanged = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    setSelectedActiveAlarms(selectedRows.slice());
  }

  const getRowId = (params: GetRowIdParams) => {
    return `${params.data.alarmid}_${params.data.datetime}`;
  }

  return (
    <div className="row alarm-table-container" style={{ height: 'inherit' }}>
      <div className="formPanel">
        <h2 className="formPanelTitle">Historical Alarms</h2>
      </div>

      <div className="col-md-12" style={{ height: 'inherit' }}>
        <div className="form-flex-row-css">
          <div>
            <Popover2
              interactionKind="click"
              placement="bottom"
              popoverClassName={Classes.POPOVER_CONTENT_SIZING}
              isOpen={dateRangePopupOpen}
              onInteraction={handleInteraction}
              minimal
              content={(
                <DateRangePicker
                  allowSingleDayRange
                  selectedShortcutIndex={selectedShortcutIndex}
                  value={selectedDateRange}
                  onChange={handleDateChange}
                  onShortcutChange={handleShortcutChange}
                />
              )}
            >
              <AnchorButton
                icon="calendar"
                rightIcon="caret-down"
                intent={Intent.PRIMARY}
                text={dateRangeButtonText}
              />
            </Popover2>
          </div>
          {/* Flex Spacer */}
          <div className="col-sm" />
          <div>
            <AnchorButton
              icon="refresh"
              intent={Intent.NONE}
              text="Refresh"
              onClick={handleRefresh}
            />
          </div>
        </div>
        <div className="ag-theme-balham common-grid-container">
          <AgGridReact
            ref={gridRef}
            columnDefs={historicalAlarmsColumnDefs}
            pagination
            paginationPageSize={PAGE_SIZE}
            rowSelection="multiple"
            rowModelType="serverSide"
            serverSideStoreType="partial"
            cacheBlockSize={PAGE_SIZE}
            onGridReady={onGridReady}
            suppressRowClickSelection
            onSelectionChanged={onSelectionChanged}
            getRowId={getRowId}
          />
        </div>
      </div>
    </div>
  );
};

export default withRouter(connector(HistoricalAlarmList));
